import { ShieldCheck, HeartHandshake, Target, Scale } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { useSiteContent } from "../hooks/useSiteContent";

const values = [
  { icon: Target, title: "Precision", desc: "Methodical sweeps, documented findings, zero guesswork." },
  { icon: HeartHandshake, title: "Integrity", desc: "Honest advice about the level of cover you actually need." },
  { icon: ShieldCheck, title: "Non-Intrusive Safety", desc: "Protection that doesn't disrupt the people it protects." },
  { icon: Scale, title: "Public Trust", desc: "Accredited teams working to British Standards, every deployment." },
];

const accreditations = ["NASDU", "BS 8517-1", "BS 8517-2", "SIA Licensed", "NCTAS-P"];

const DEFAULT_GREEN_BODY = `QUEST K9's was built on the belief that a potential working dog doesn't always look like one.

Many of the dogs we work with come from rescue or rehoming situations, often from ordinary family homes and with little or no previous working experience. Some arrive lacking confidence, environmental exposure or even the basic foundations needed to engage with the world around them.

## Perception is key.

Finding the right dog isn't always about a formal assessment. It is about looking beyond the obvious. Reading the information provided, asking the right questions, understanding the dog's history and watching how it responds to simple things such as play, a ball or a tug toy.

Sometimes it's instinct. Sometimes it's intuition. Sometimes it's simply knowing when a dog has something worth developing.

From there, the focus is on environmental development, boundaries, structured play, confidence and engagement. A dog may need to learn how to navigate stairs, climb onto different surfaces or experience environments it has never previously encountered. These are not limitations. They are opportunities to build confidence and develop the dog's natural enthusiasm.

## Because detection work should not feel like a job to the dog.

The dog needs to love the game.

That enthusiasm, the desire to search and the pleasure in finding the odour are fundamental to developing a successful working dog. Our approach is to keep the work within the working dog, allowing the dog to be a dog while developing the skills required for operational work.

Over the past 2 years, this approach has seen more than 20 dogs successfully developed and placed into operational roles with organisations including the British Armed Forces, UK police forces, Border Force and international canine organisations.`;

function StoryBlocks({ text, empty }) {
  const blocks = (text || "").split("\n\n").filter((b) => b.trim());
  if (!blocks.length) return <p className="text-slate-400">{empty}</p>;
  return (
    <div className="space-y-5">
      {blocks.map((b, i) =>
        b.startsWith("## ") ? (
          <h3 key={i} className="pt-3 font-display font-bold text-lg sm:text-xl text-brand-crimson tracking-tight">{b.slice(3)}</h3>
        ) : (
          <p key={i} className="text-slate-300 leading-relaxed text-base sm:text-lg">{b}</p>
        )
      )}
    </div>
  );
}

export default function About() {
  const content = useSiteContent();
  const about = content?.about || {};
  const pg = content?.pages?.about_page || {};
  const story = Array.isArray(about.story) ? about.story.join("\n\n") : "";
  const green = {
    eyebrow: pg.green_eyebrow || "QUEST K9's Green Dogs",
    title: pg.green_title || "Giving the underdog",
    accent: pg.green_accent || "a chance.",
    body: pg.green_body || DEFAULT_GREEN_BODY,
    quote: pg.green_quote || "The underdog isn't necessarily the wrong dog. Sometimes it just takes the right person to see the potential",
    image: pg.green_image || "/logo.png",
  };

  return (
    <main className="bg-brand-obsidian pt-28 sm:pt-32" data-testid="about-page">
      <section className="px-4 sm:px-6 pb-24">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <Eyebrow className="flex items-center gap-3"><span className="h-px w-10 bg-brand-crimson inline-block" /> {pg.eyebrow || "About QUEST K9's"}</Eyebrow>
            <h1 className="mt-4 font-display font-extrabold uppercase text-3xl sm:text-5xl text-white tracking-tight">
              {about.title || "Built on knowledge. Driven by experience."}
            </h1>
          </Reveal>

          <div className="mt-12 grid lg:grid-cols-2 gap-12 items-start">
            <Reveal>
              <div className="space-y-5" data-testid="about-story">
                <StoryBlocks text={story} empty="Our story is being written - come back soon." />
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="relative group" data-testid="about-image-frame">
                <div className="absolute -inset-2 bg-gradient-to-br from-brand-crimson/40 to-transparent rounded-lg blur-lg opacity-60 group-hover:opacity-100 transition-opacity" />
                <img
                  src={about.image}
                  alt="Quest K9's detection dog"
                  className="relative rounded-lg border border-brand-line w-full h-[380px] object-cover"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6" data-testid="green-dogs-section">
        <div className="mx-auto max-w-7xl grid lg:grid-cols-[1.4fr_1fr] gap-12 items-start">
          <div>
            <Reveal>
              <Eyebrow className="flex items-center gap-3"><span className="h-px w-10 bg-brand-crimson inline-block" /> {green.eyebrow}</Eyebrow>
              <h2 className="mt-4 font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight">
                {green.title} <span className="text-brand-crimson">{green.accent}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8" data-testid="green-dogs-body">
                <StoryBlocks text={green.body} />
              </div>
            </Reveal>
          </div>
          <div className="space-y-6 lg:sticky lg:top-28">
            <Reveal delay={0.15}>
              <div className="rounded-lg border border-brand-line bg-brand-surface p-8 text-center">
                <img src={green.image} alt="QUEST K9's Detection Dogs crest" className="mx-auto w-40 rounded-lg" />
                <p className="mt-6 font-eyebrow text-[11px] uppercase tracking-[0.25em] font-bold text-white/50">2 years &middot; 20+ dogs placed</p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <blockquote className="rounded-lg border-l-4 border-brand-crimson bg-brand-surface p-7" data-testid="green-dogs-quote">
                <p className="font-display text-lg sm:text-xl text-white leading-relaxed">&ldquo;{green.quote}&rdquo;</p>
              </blockquote>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 bg-brand-surface/40 border-y border-brand-line" data-testid="values-section">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <Eyebrow className="flex items-center gap-3"><span className="h-px w-10 bg-brand-crimson inline-block" /> {pg.values_eyebrow || "What We Stand For"}</Eyebrow>
            <h2 className="mt-4 font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight">{pg.values_title || "Values that walk the walk"}</h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full rounded-lg border border-brand-line bg-brand-obsidian p-7 hover:border-brand-crimson/60 transition-colors duration-300" data-testid={`value-card-${i}`}>
                  <v.icon className="h-7 w-7 text-brand-crimson" />
                  <h3 className="mt-5 text-lg font-semibold text-white">{v.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6" data-testid="accreditations-section">
        <div className="mx-auto max-w-7xl text-center">
          <Reveal>
            <Eyebrow>{pg.acc_eyebrow || "Accreditation & Compliance"}</Eyebrow>
            <h2 className="mt-4 font-display font-bold text-2xl sm:text-3xl text-white tracking-tight">{pg.acc_title || "Fully credentialed. Fully insured."}</h2>
          </Reveal>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            {accreditations.map((a, i) => (
              <Reveal key={a} delay={i * 0.06}>
                <div
                  data-testid={`accreditation-badge-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="px-8 py-5 rounded-lg border border-brand-line bg-brand-surface font-eyebrow uppercase tracking-[0.2em] font-bold text-white/80 hover:text-white hover:border-brand-crimson/70 transition-all duration-300"
                >
                  {a}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
